import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  GridItem,
  Image,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  Divider,
  useToast,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  List,
  ListItem,
  ListIcon,
  Link,
} from "@chakra-ui/react";
import { PhoneIcon, EmailIcon, InfoIcon, LockIcon } from "@chakra-ui/icons";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { useProducts } from "../../contexts/ProductContext";
import { useAuth } from "../../contexts/AuthContext";
import { resolveProductImageUrl } from "../../services/api";
import { WhatsappLogoIcon } from "@phosphor-icons/react";

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { products } = useProducts();
  const { user } = useAuth();
  const textColor = useColorModeValue("green.600", "green.300");
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Find the product with the matching ID
  const product = products.find((p) => p._id === id);

  React.useEffect(() => {
    if (!product) {
      toast({
        title: "Product Not Found",
        description: "The product you are looking for does not exist.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      navigate("/marketplace");
    }
  }, [product, toast, navigate]);

  const handleContact = () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description:
          "Please log in or register as a buyer to contact the farmer.",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
      navigate("/login");
      return;
    }

    if (user.role !== "buyer") {
      toast({
        title: "Access Restricted",
        description:
          "Only registered buyers can view farmer contact information.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      navigate("/register?type=buyer");
      return;
    }

    if (!product) {
      toast({
        title: "Error",
        description: "Product information not found.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsContactModalOpen(true);
  };

  if (!product) {
    return null;
  }

  return (
    <Box py={8}>
      <Container maxW="container.xl">
        <Button mb={8} onClick={() => navigate("/marketplace")} variant="ghost">
          <Box as="span" fontSize="2xl" mr={2}>←</Box> Back to Marketplace
        </Button>

        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
          {/* Product Image */}
          <GridItem>
            <Image
              src={resolveProductImageUrl(product.imageUrl)}
              alt={product.name}
              borderRadius="lg"
              w="full"
              h="400px"
              objectFit="cover"
              fallbackSrc="/images/product-placeholder.jpg"
            />
          </GridItem>

          {/* Product Information */}
          <GridItem>
            <VStack align="stretch" spacing={4}>
              <Heading size="xl">{product.name}</Heading>

              <HStack>
                <Badge colorScheme="green" fontSize="md" px={2} py={1}>
                  {product.category}
                </Badge>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  ${product?.price}/{product?.unit}
                </Text>
              </HStack>

              <Divider />

              <VStack align="stretch" spacing={2}>
                <Text fontSize="lg" fontWeight="semibold">
                  Details:
                </Text>
                <HStack justify="space-between">
                  <Text>Available Quantity:</Text>
                  <Text>
                    {product.quantity} {product.unit}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>Location:</Text>
                  <Text>{product.farmLocation}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>Harvest Date:</Text>
                  <Text>
                    {product.harvestDate
                      ? new Date(product.harvestDate).toLocaleDateString(
                          undefined,
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          },
                        )
                      : "Not specified"}
                  </Text>
                </HStack>
              </VStack>

              <Divider />

              <VStack align="stretch" spacing={2}>
                <Text fontSize="lg" fontWeight="semibold">
                  Description:
                </Text>
                <Text>{product.description}</Text>
              </VStack>

              <Divider />

              <VStack align="stretch" spacing={2}>
                <Text fontSize="lg" fontWeight="semibold">
                  Seller Information:
                </Text>
                <Text>Farmer: {product.farmerName}</Text>
                <Text fontSize="sm" color="gray.500">
                  Listed on:{" "}
                  {product.createdAt
                    ? new Date(product.createdAt).toLocaleDateString(
                        undefined,
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )
                    : "Recently"}
                </Text>
              </VStack>

              <HStack spacing={3} mt={4} w="full">
                <Button
                  colorScheme="brand"
                  size="lg"
                  onClick={handleContact}
                  flex={1}
                  leftIcon={<PhoneIcon />}
                >
                  Contact Farmer
                </Button>
                {(() => {
                  const whatsappNum = product.farmer?.whatsApp || product.farmer?.phoneNumber;
                  if (!whatsappNum) return null;
                  const cleanNum = whatsappNum.replace(/\D/g, "");
                  const waUrl = `https://wa.me/${cleanNum}?text=${encodeURIComponent(
                    `Hello ${product.farmerName},\n\nI am interested in your ${product.name} listed on MarketLink Marketplace.\n\nProduct Details:\n- Price: $${product.price}/${product.unit}\n- Location: ${product.farmLocation}\n\nPlease let me know if this product is still available.\n\nThank you!`
                  )}`;
                  return (
                    <Button
                      as="a"
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      colorScheme="whatsapp"
                      size="lg"
                      flex={1}
                      leftIcon={<WhatsappLogoIcon fontSize="20px" />}
                      onClick={(e) => {
                        if (!user || user.role !== "buyer") {
                          e.preventDefault();
                          handleContact();
                        }
                      }}
                    >
                      WhatsApp
                    </Button>
                  );
                })()}
              </HStack>
            </VStack>
          </GridItem>
        </Grid>

        {/* Contact Modal */}
        <Modal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          size="lg"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Contact Farmer</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold">Farmer Name:</Text>
                <Text>{product.farmerName}</Text>

                {user && user.role === "buyer" ? (
                  <>
                    <Text fontWeight="bold">Contact Information:</Text>
                    <List spacing={4}>
                      {product.farmer?.phoneNumber ? (
                        <ListItem>
                          <HStack>
                            <ListIcon as={PhoneIcon} color="green.500" />
                            <VStack align="start" spacing={1}>
                              <Text
                                fontWeight="semibold"
                                fontSize="sm"
                                color="gray.600"
                              >
                                Phone:
                              </Text>
                              <Link
                                href={`tel:${product.farmer.phoneNumber}`}
                                color="green.600"
                                fontWeight="bold"
                                fontSize="lg"
                                _hover={{
                                  color: "green.500",
                                  textDecoration: "underline",
                                }}
                              >
                                {product.farmer.phoneNumber}
                              </Link>
                              <Text fontSize="xs" color="gray.500">
                                Click to call directly
                              </Text>
                            </VStack>
                          </HStack>
                        </ListItem>
                      ) : (
                        <ListItem>
                          <HStack>
                            <ListIcon as={PhoneIcon} color="gray.400" />
                            <Text color="gray.500">
                              No phone number available
                            </Text>
                          </HStack>
                        </ListItem>
                      )}

                      {(() => {
                        const whatsappNum = product.farmer?.whatsApp || product.farmer?.phoneNumber;
                        if (!whatsappNum) {
                          return (
                            <ListItem>
                              <HStack>
                                <ListIcon as={InfoIcon} color="gray.400" />
                                <Text color="gray.500">No WhatsApp available</Text>
                              </HStack>
                            </ListItem>
                          );
                        }
                        const cleanNum = whatsappNum.replace(/\D/g, "");
                        const waUrl = `https://wa.me/${cleanNum}?text=${encodeURIComponent(
                          `Hello ${product.farmerName},\n\nI am interested in your ${product.name} listed on MarketLink Marketplace.\n\nProduct Details:\n- Price: $${product.price}/${product.unit}\n- Location: ${product.farmLocation}\n\nPlease let me know if this product is still available.\n\nThank you!`
                        )}`;
                        return (
                          <ListItem>
                            <HStack align="start">
                              <ListIcon as={WhatsappLogoIcon} color="green.500" fontSize="xl" mt={1} />
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                                  WhatsApp:
                                </Text>
                                <Link
                                  href={waUrl}
                                  isExternal
                                  color="green.600"
                                  fontWeight="bold"
                                  fontSize="md"
                                  wordBreak="break-all"
                                  _hover={{
                                    color: "green.500",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {whatsappNum}
                                </Link>
                                <Button
                                  as="a"
                                  href={waUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="sm"
                                  colorScheme="whatsapp"
                                  leftIcon={<WhatsappLogoIcon fontSize="lg" />}
                                  mt={1}
                                >
                                  Chat on WhatsApp
                                </Button>
                              </VStack>
                            </HStack>
                          </ListItem>
                        );
                      })()}
                    </List>
                  </>
                ) : (
                  <Box bg="yellow.50" p={4} borderRadius="md">
                    <HStack>
                      <InfoIcon color="yellow.500" />
                      <Text color="yellow.700">
                        Register as a buyer to view farmer contact information
                      </Text>
                    </HStack>
                  </Box>
                )}

                <Text fontWeight="bold" mt={4}>
                  Product:
                </Text>
                <HStack>
                  <Badge colorScheme="green" fontSize="sm" px={2} py={1}>
                    {product.name}
                  </Badge>
                  <Text fontWeight="semibold" color="green.600">
                    ${product.price}/{product.unit}
                  </Text>
                </HStack>

                <Box bg="blue.50" p={4} borderRadius="md" mt={2}>
                  <HStack align="start">
                    <InfoIcon color="blue.500" mt={1} />
                    <VStack align="start" spacing={1}>
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="blue.700"
                      >
                        Communication Guidelines:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        • Be respectful.
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        • Contact during business hours (8 AM - 6 PM)
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        • Clearly state your inquiry and requirements
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        • Do not abuse the platform
                      </Text>
                    </VStack>
                  </HStack>
                </Box>

                <Box
                  bg="green.50"
                  p={4}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="green.200"
                >
                  <HStack>
                    <LockIcon color="green.600" />
                    <Text fontSize="sm" color="green.700" fontWeight="medium">
                      Privacy Protected: Contact the farmer during business hours
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="brand"
                onClick={() => setIsContactModalOpen(false)}
              >
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
};

export default ProductDetails;
